import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  closestCenter,
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import useAppUtils from '@/utils/appUtils';

import type { VNSortableItemTemplateProps } from './VNSortableItem';
import VNSortableItem from './VNSortableItem';

type Props = {
  name: string;
  direction?: 'vertical' | 'horizontal';
  // post sort action
  onSorted?: (oldIndex: number, newIndex: number) => void;
  /**
   * - string: message to confirm deletion
   * - function: gets custom message to confirm deletion
   * - true: delete without confirmation
   * - false/undefined: do not delete
   */
  deletable?: ((item: any) => string) | string | boolean;
  // check if item is deletable
  isItemDeletable?: (item: any) => boolean;
  // post delete action
  onDeleted?: (item: any) => void;
  onEdit?: (item: number) => void;
  itemTemplate?: React.ComponentType<VNSortableItemTemplateProps>;
  draggingLabel?: React.ComponentType<VNSortableItemTemplateProps>;
  itemTemplateProps?: Record<string, any>;
  sortField?: string;
  deleteDisabledTooltip?: string;
};

const directionOptions = {
  horizontal: {
    classes: 'vn-sortable horizontal',
    strategy: horizontalListSortingStrategy,
    collision: closestCorners,
  },
  vertical: {
    classes: 'vn-sortable vertical',
    strategy: verticalListSortingStrategy,
    collision: closestCenter,
  },
};

const VNSortable = ({
  name,
  draggingLabel,
  direction = 'vertical',
  onSorted,
  deletable,
  isItemDeletable,
  onDeleted,
  onEdit,
  itemTemplate,
  itemTemplateProps,
  sortField,
  deleteDisabledTooltip,
}: Props) => {
  const { confirm } = useAppUtils();
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const { control } = useFormContext();
  const { fields, move, remove } = useFieldArray<any, string, '_id'>({
    control,
    name,
    keyName: '_id',
  });

  const editedItems: any = useWatch({
    control,
    name,
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 75,
        tolerance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 75,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (event.active.data.current.sortable.index !== -1) {
      setActiveItemIndex(event.active.data.current.sortable.index);
    }
  };

  const moveAction = (oldIndex: number, newIndex: number) => {
    move(oldIndex, newIndex);
    arrayMove(fields, oldIndex, newIndex);
    onSorted?.(oldIndex, newIndex);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItemIndex(null);
    const { active, over } = event;

    if (!active || !over) {
      return;
    }
    const oldIndex = fields.findIndex((i: any) => i._id === active.id);
    const newIndex = fields.findIndex((i: any) => i._id === over.id);

    moveAction(oldIndex, newIndex);
  };

  const handleMove = (currentIndex: number) => (dir: number | 'first' | 'last') => {
    let newIndex = 0;
    if (dir === 'last') {
      newIndex = fields.length - 1;
    } else if (typeof dir === 'number') {
      newIndex = currentIndex + dir;
    }

    moveAction(currentIndex, newIndex);
  };

  const handleRemove = (index: number) => async () => {
    let confirmed;
    if (typeof deletable === 'string') {
      confirmed = await confirm(deletable);
    } else if (typeof deletable === 'function') {
      const deletableMessage = deletable(editedItems[index]);
      confirmed = await confirm(deletableMessage);
    } else {
      confirmed = deletable;
    }

    if (confirmed) {
      remove(index);
      onDeleted?.(editedItems[index]);
    }
  };

  useEffect(() => {
    if (!sortField) {
      return;
    }

    // dont trigger state/form changes
    editedItems.forEach((item: any, index: number) => {
      item[sortField] = index;
    });
  }, [editedItems, sortField]);

  return (
    <div className={directionOptions[direction].classes}>
      <DndContext
        sensors={sensors}
        collisionDetection={directionOptions[direction].collision}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <SortableContext items={fields.map((f) => f._id)} strategy={directionOptions[direction].strategy}>
          {editedItems?.length > 0 &&
            fields?.map((item, index) => (
              <VNSortableItem
                key={item._id}
                itemId={item._id}
                editedItem={editedItems[index]}
                index={index}
                parentName={name}
                ItemTemplate={itemTemplate}
                itemTemplateProps={itemTemplateProps}
                handleMove={handleMove(index)}
                handleRemove={deletable && handleRemove(index)}
                handleEdit={onEdit && (() => onEdit(index))}
                canDelete={!isItemDeletable || isItemDeletable(editedItems[index])}
                deleteDisabledTooltip={deleteDisabledTooltip}
              />
            ))}
        </SortableContext>

        <DragOverlay className="vn-sortable-overlay">
          {activeItemIndex !== null && (
            <VNSortableItem
              key={fields[activeItemIndex]._id}
              parentName={name}
              itemId={fields[activeItemIndex]._id}
              editedItem={editedItems[activeItemIndex]}
              index={activeItemIndex}
              ItemTemplate={draggingLabel || itemTemplate}
              itemTemplateProps={itemTemplateProps}
            />
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default VNSortable;
